/**
 * Role Builder — simplified session-first workflow with guided intake and structured artifact previews.
 * Roles are simple mutable records. Template config is tenant-level (managed via Settings page).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Cog6ToothIcon,
  DocumentTextIcon,
  PlusIcon,
  Square2StackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { AXIOS_INSTANCE } from '../../services/api-client';
import {
  Page,
  PageHeader,
  PageBody,
  Alert,
  Spinner,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelDescription,
  PanelBody,
  PanelFooter,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Label,
  Input,
  Textarea,
  Badge,
  Chip,
  ChipGroup,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui';

const DRAFT_DEBOUNCE_MS = 700;

type JsonRecord = Record<string, unknown>;
type WizardStepId = 'basics' | 'skills' | 'competencies';

interface BootstrapCompetency {
  key: string;
  description: string;
}

interface RoleListItem {
  id: number;
  title: string;
  status: string;
  updated_at: string;
}

interface DraftShape {
  role_title: string;
  why_exists: string;
  kpis: string[];
  why_join: string;
  hard_skills: string[];
  competencies: string[];
}

interface RoleDetail {
  id: number;
  config_id: number;
  title: string;
  status: string;
  current_draft: DraftShape;
  generated_jd: JsonRecord | null;
  generated_interview_kit: JsonRecord | null;
  generated_copy_text: Record<string, string> | null;
  config_revision_id: number | null;
}

function formatApiDetail(err: unknown, fallback: string): string {
  const r = err as { response?: { data?: { detail?: unknown } } };
  const d = r.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return (
      d
        .map((x) =>
          typeof x === 'string' ? x : (x as { msg?: string })?.msg || JSON.stringify(x)
        )
        .join('; ') || fallback
    );
  }
  return fallback;
}

const emptyDraft: DraftShape = {
  role_title: '',
  why_exists: '',
  kpis: [],
  why_join: '',
  hard_skills: [],
  competencies: [],
};

const sessionSteps: Array<{ id: WizardStepId; label: string; helper: string }> = [
  { id: 'basics', label: 'The Role', helper: 'Name the role, explain why it exists, and describe why someone would join.' },
  { id: 'skills', label: 'The Candidate', helper: 'Define the KPIs and must-have skills for this role.' },
  { id: 'competencies', label: 'Competencies', helper: 'Select the core competencies (3–5) to assess in the interview process.' },
];

function roleStatusLabel(status: string): string {
  return status === 'generated' ? 'Generated' : 'Draft';
}

function roleStatusVariant(status: string): 'secondary' | 'success' {
  return status === 'generated' ? 'success' : 'secondary';
}

function formatRoleUpdatedAt(value: string): string {
  const date = new Date(value);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function draftFromApi(raw: Partial<DraftShape> | undefined): DraftShape {
  if (!raw || typeof raw !== 'object') return { ...emptyDraft };
  return {
    role_title: String(raw.role_title ?? ''),
    why_exists: String(raw.why_exists ?? ''),
    kpis: Array.isArray(raw.kpis) ? raw.kpis.map((x) => String(x)) : [],
    why_join: String(raw.why_join ?? ''),
    hard_skills: Array.isArray(raw.hard_skills) ? raw.hard_skills.map((x) => String(x)) : [],
    competencies: Array.isArray(raw.competencies) ? raw.competencies.map((x) => String(x)) : [],
  };
}

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;
}

function asRecordArray(value: unknown): JsonRecord[] {
  return Array.isArray(value) ? value.map((item) => asRecord(item)).filter(Boolean) as JsonRecord[] : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function renderArtifactBlocks(blocks: JsonRecord[]) {
  return blocks.map((block, index) => {
    const type = asString(block.type);
    if (type === 'paragraph') {
      return (
        <p key={index} className="text-sm leading-6 text-gray-700 dark:text-gray-300">
          {asString(block.text)}
        </p>
      );
    }
    if (type === 'subheading') {
      return (
        <h4 key={index} className="text-sm font-semibold text-charcoal dark:text-gray-100">
          {asString(block.text)}
        </h4>
      );
    }
    if (type === 'list') {
      return (
        <ul key={index} className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-disc pl-5">
          {asStringArray(block.items).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    }
    if (type === 'kpi_grid') {
      return (
        <div key={index} className="grid gap-3 md:grid-cols-2">
          {asStringArray(block.items).map((item) => (
            <div
              key={item}
              className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-charcoal dark:border-dark-border/50 dark:bg-dark-surface-2 dark:text-gray-100"
            >
              {item}
            </div>
          ))}
        </div>
      );
    }
    if (type === 'competency_badges') {
      return (
        <div key={index} className="flex flex-wrap gap-2">
          {asStringArray(block.names).map((name) => (
            <Badge key={name} variant="secondary">
              {name}
            </Badge>
          ))}
        </div>
      );
    }
    if (type === 'questions') {
      return (
        <ol key={index} className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-decimal pl-5">
          {asStringArray(block.items).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      );
    }
    if (type === 'rubric') {
      return (
        <div key={index} className="space-y-2">
          {asRecordArray(block.rows).map((row, rowIndex) => (
            <div
              key={`${asString(row.score)}-${rowIndex}`}
              className="flex gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm dark:border-dark-border/50"
            >
              <div className="w-10 shrink-0 font-semibold text-charcoal dark:text-gray-100">
                {asString(row.score)}
              </div>
              <div className="text-gray-700 dark:text-gray-300">{asString(row.label)}</div>
            </div>
          ))}
        </div>
      );
    }
    if (type === 'meta') {
      return (
        <p key={index} className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {asString(block.text)}
        </p>
      );
    }
    if (type === 'competency_guides') {
      return (
        <div key={index} className="space-y-4">
          {asRecordArray(block.items).map((item, guideIndex) => (
            <Card key={`${asString(item.competency)}-${guideIndex}`}>
              <CardHeader>
                <CardTitle>{asString(item.competency)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {renderArtifactBlocks(asRecordArray(item.blocks))}
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    return null;
  });
}

function JobDescriptionView({ document }: { document: JsonRecord }) {
  const sections = asRecordArray(document.sections);
  const header = sections.find((section) => asString(section.id) === 'header');
  return (
    <div className="space-y-6">
      {header && (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
          <h3 className="font-title text-xl text-charcoal dark:text-gray-100">
            {asString(header.title)}
          </h3>
          <div className="mt-2 space-y-2">{renderArtifactBlocks(asRecordArray(header.blocks))}</div>
        </div>
      )}
      {sections
        .filter((section) => asString(section.id) !== 'header')
        .map((section, index) => (
          <Card key={`${asString(section.id)}-${index}`}>
            <CardHeader>
              <CardTitle>{asString(section.title) || 'Details'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderArtifactBlocks(asRecordArray(section.blocks))}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}

function InterviewStageView({ stage }: { stage: JsonRecord }) {
  const subsections = asRecordArray(stage.subsections);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-eliza-red/15 bg-eliza-red/5 px-5 py-4 dark:border-eliza-red/25 dark:bg-eliza-red/10">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Stage {String(stage.stage ?? '')}
        </p>
        <h4 className="mt-1 font-title text-xl text-charcoal dark:text-gray-100">
          {asString(stage.title)}
        </h4>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{asString(stage.meta)}</p>
      </div>

      {asStringArray(stage.questions).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Questions</CardTitle>
          </CardHeader>
          <CardContent>{renderArtifactBlocks([{ type: 'questions', items: asStringArray(stage.questions) }])}</CardContent>
        </Card>
      )}

      {subsections.map((subsection, index) => (
        <Card key={`${asString(subsection.heading)}-${index}`}>
          <CardHeader>
            <CardTitle>{asString(subsection.heading) || 'Section'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {asString(subsection.paragraph) && (
              <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
                {asString(subsection.paragraph)}
              </p>
            )}
            {asStringArray(subsection.questions).length > 0 &&
              renderArtifactBlocks([{ type: 'questions', items: asStringArray(subsection.questions) }])}
            {asRecordArray(subsection.competencies).map((competency, competencyIndex) => (
              <div
                key={`${asString(competency.name)}-${competencyIndex}`}
                className="rounded-xl border border-gray-200 px-4 py-4 dark:border-dark-border/50"
              >
                <div className="mb-3 flex items-center gap-2">
                  <Badge variant="secondary">{asString(competency.name)}</Badge>
                </div>
                {renderArtifactBlocks([{ type: 'questions', items: asStringArray(competency.questions) }])}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function InterviewKitView({ interviewKit }: { interviewKit: JsonRecord }) {
  const sections = asRecordArray(interviewKit.sections);
  const roleOverview = sections.find((section) => asString(section.id) === 'role-overview');
  const process = sections.find((section) => asString(section.id) === 'interview-process');
  const guide = sections.find((section) => asString(section.id) === 'competency-guide');
  const footer = sections.find((section) => asString(section.id) === 'footer');
  const stages = asRecordArray(process?.stages);
  const overviewBlocks = asRecordArray(roleOverview?.blocks);
  const competencyBlock = overviewBlocks.find((block) => asString(block.type) === 'competency_badges');
  const competencies = asStringArray(competencyBlock?.names);

  return (
    <Tabs defaultValue="overview">
      <TabsList variant="pills" className="mb-5 flex flex-wrap">
        <TabsTrigger value="overview" variant="pills">
          Overview
        </TabsTrigger>
        <TabsTrigger value="process" variant="pills">
          Interview process
        </TabsTrigger>
        {stages.map((stage) => (
          <TabsTrigger
            key={`stage-tab-${String(stage.stage)}`}
            value={`stage-${String(stage.stage)}`}
            variant="pills"
          >
            Stage {String(stage.stage)}
          </TabsTrigger>
        ))}
        <TabsTrigger value="rubric" variant="pills">
          Rubric
        </TabsTrigger>
        <TabsTrigger value="debrief" variant="pills">
          Debrief notes
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="py-5">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Interview stages</p>
              <p className="mt-2 font-title text-lg text-charcoal dark:text-gray-100">
                {stages.length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-5">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Competencies assessed</p>
              <p className="mt-2 font-title text-lg text-charcoal dark:text-gray-100">
                {competencies.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {competencies.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Assessment focus</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {competencies.map((competency) => (
                  <Badge key={competency} variant="secondary">
                    {competency}
                  </Badge>
                ))}
              </div>
              {renderArtifactBlocks(overviewBlocks.filter((block) => asString(block.type) !== 'competency_badges'))}
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="process" className="space-y-4">
        <div className="grid gap-4 xl:grid-cols-2">
          {stages.map((stage) => (
            <Card key={`stage-card-${String(stage.stage)}`}>
              <CardHeader>
                <CardTitle>
                  Stage {String(stage.stage)}: {asString(stage.title)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">{asString(stage.meta)}</p>
                {asStringArray(stage.questions).slice(0, 2).map((question) => (
                  <div
                    key={question}
                    className="rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 dark:border-dark-border/50 dark:text-gray-300"
                  >
                    {question}
                  </div>
                ))}
                {asRecordArray(stage.subsections).map((subsection, index) => (
                  <div key={`${asString(subsection.heading)}-${index}`} className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-charcoal dark:text-gray-100">
                      {asString(subsection.heading)}
                    </span>
                    {': '}
                    {asStringArray(subsection.questions)[0] || asString(subsection.paragraph) || `${asRecordArray(subsection.competencies).length} competency prompts`}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </TabsContent>

      {stages.map((stage) => (
        <TabsContent key={`stage-content-${String(stage.stage)}`} value={`stage-${String(stage.stage)}`}>
          <InterviewStageView stage={stage} />
        </TabsContent>
      ))}

      <TabsContent value="rubric">
        <div className="space-y-4">
          {guide ? (
            renderArtifactBlocks(asRecordArray(guide.blocks))
          ) : (
            <Alert variant="info" title="Rubric unavailable">
              This role does not yet include structured rubric guidance.
            </Alert>
          )}
          {footer && (
            <Card>
              <CardContent className="py-4 text-sm text-muted-foreground">
                {renderArtifactBlocks(asRecordArray(footer.blocks))}
              </CardContent>
            </Card>
          )}
        </div>
      </TabsContent>

      <TabsContent value="debrief">
        <Card>
          <CardHeader>
            <CardTitle>Debrief notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Use this structured summary when the panel reconvenes after the interview loop.
            </p>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-disc pl-5">
              <li>What evidence did we hear for each core competency?</li>
              <li>Which stage surfaced the strongest proof points or concerns?</li>
              <li>What skills or behaviors still need follow-up before a decision?</li>
              <li>Would we trust this candidate to operate successfully in this role within 90 days?</li>
            </ul>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

export function RoleBuilderPage() {
  const navigate = useNavigate();
  const [competencies, setCompetencies] = useState<BootstrapCompetency[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleDetail, setRoleDetail] = useState<RoleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState<DraftShape>(emptyDraft);
  const [kpisText, setKpisText] = useState('');
  const [skillsText, setSkillsText] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateBusy, setGenerateBusy] = useState(false);
  const [activeStep, setActiveStep] = useState<WizardStepId>('basics');
  const [createError, setCreateError] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'edit' | 'view'>('edit');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<DraftShape | null>(null);
  const pendingDraftRoleIdRef = useRef<number | null>(null);
  const inFlightDraftSaveRef = useRef<Promise<boolean> | null>(null);

  const loadBootstrap = useCallback(async () => {
    setBootstrapError(null);
    try {
      const res = await AXIOS_INSTANCE.get<{
        competencies: BootstrapCompetency[];
        permissions: { can_edit: boolean };
      }>('/api/v1/role-builder/bootstrap');
      setCompetencies(res.data.competencies);
      setCanEdit(res.data.permissions.can_edit);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'Bootstrap failed';
      setBootstrapError(msg);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await AXIOS_INSTANCE.get<{ items: RoleListItem[] }>('/api/v1/role-builder/roles');
      setRoles(res.data.items);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const loadRoleDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setSaveError(null);
    setGenerateError(null);
    try {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pendingDraftRef.current = null;
      pendingDraftRoleIdRef.current = null;
      const res = await AXIOS_INSTANCE.get<RoleDetail>(`/api/v1/role-builder/roles/${id}`);
      setRoleDetail(res.data);
      const nextDraft = draftFromApi(res.data.current_draft);
      if (!nextDraft.role_title && res.data.title) {
        nextDraft.role_title = res.data.title;
      }
      setDraft(nextDraft);
      setKpisText(nextDraft.kpis.join('\n'));
      setSkillsText(nextDraft.hard_skills.join('\n'));
      setActiveStep('basics');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (selectedRoleId != null) {
      void loadRoleDetail(selectedRoleId);
    } else {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pendingDraftRef.current = null;
      pendingDraftRoleIdRef.current = null;
      setRoleDetail(null);
      setDraft(emptyDraft);
      setKpisText('');
      setSkillsText('');
    }
  }, [selectedRoleId, loadRoleDetail]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const flushPendingDraftSave = useCallback(async (): Promise<boolean> => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const next = pendingDraftRef.current;
    const roleId = pendingDraftRoleIdRef.current;
    const pendingSave = inFlightDraftSaveRef.current;

    if (!canEdit || roleId == null || next == null) {
      if (pendingSave) {
        return pendingSave;
      }
      return true;
    }

    pendingDraftRef.current = null;
    pendingDraftRoleIdRef.current = null;

    let savePromise: Promise<boolean> | null = null;
    savePromise = (async () => {
      if (pendingSave) {
        await pendingSave;
      }

      try {
        setSaveError(null);
        const payload: { current_draft: DraftShape; title?: string } = { current_draft: next };
        if (next.role_title) {
          payload.title = next.role_title;
        }
        await AXIOS_INSTANCE.put(`/api/v1/role-builder/roles/${roleId}`, payload);
        await loadRoles();
        return true;
      } catch (err: unknown) {
        setSaveError(formatApiDetail(err, 'Draft autosave failed'));
        return false;
      } finally {
        if (savePromise && inFlightDraftSaveRef.current === savePromise) {
          inFlightDraftSaveRef.current = null;
        }
      }
    })();

    inFlightDraftSaveRef.current = savePromise;
    return savePromise;
  }, [canEdit, loadRoles]);

  const pushDraftSave = useCallback(
    (next: DraftShape) => {
      if (!canEdit || selectedRoleId == null) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingDraftRef.current = next;
      pendingDraftRoleIdRef.current = selectedRoleId;
      debounceRef.current = setTimeout(() => {
        void flushPendingDraftSave();
      }, DRAFT_DEBOUNCE_MS);
    },
    [canEdit, selectedRoleId, flushPendingDraftSave]
  );

  const setDraftField = (partial: Partial<DraftShape>) => {
    setDraft((prev) => {
      const merged = { ...prev, ...partial };
      pushDraftSave(merged);
      return merged;
    });
  };

  const onKpisBlur = () => {
    setDraftField({
      kpis: kpisText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    });
  };

  const onSkillsBlur = () => {
    setDraftField({
      hard_skills: skillsText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    });
  };

  const toggleCompetency = (key: string) => {
    setDraft((prev) => {
      let next = [...prev.competencies];
      if (next.includes(key)) {
        next = next.filter((item) => item !== key);
      } else if (next.length < 5) {
        next.push(key);
      }
      pushDraftSave({ ...prev, competencies: next });
      return { ...prev, competencies: next };
    });
  };

  const createRole = async () => {
    if (!canEdit) return;
    setCreateError(null);
    try {
      const res = await AXIOS_INSTANCE.post<RoleDetail>('/api/v1/role-builder/roles', {
        title: 'Untitled Role',
      });
      await loadRoles();
      setSelectedRoleId(res.data.id);
      setRightTab('edit');
    } catch (e: unknown) {
      setCreateError(formatApiDetail(e, 'Could not create role'));
    }
  };

  const duplicateRole = async () => {
    if (selectedRoleId == null || !canEdit) return;
    try {
      const res = await AXIOS_INSTANCE.post<RoleDetail>(
        `/api/v1/role-builder/roles/${selectedRoleId}/duplicate`
      );
      await loadRoles();
      setSelectedRoleId(res.data.id);
    } catch (e: unknown) {
      setGenerateError(formatApiDetail(e, 'Could not duplicate role'));
    }
  };

  const deleteRole = async () => {
    if (selectedRoleId == null || !canEdit) return;
    if (!window.confirm('Delete this role? This cannot be undone.')) return;
    try {
      await AXIOS_INSTANCE.delete(`/api/v1/role-builder/roles/${selectedRoleId}`);
      setSelectedRoleId(null);
      await loadRoles();
    } catch (e: unknown) {
      setGenerateError(formatApiDetail(e, 'Could not delete role'));
    }
  };

  const runGenerate = async () => {
    if (selectedRoleId == null || !canEdit) return;
    setGenerateBusy(true);
    setGenerateError(null);
    try {
      const draftSaved = await flushPendingDraftSave();
      if (!draftSaved) {
        return;
      }
      await AXIOS_INSTANCE.post(`/api/v1/role-builder/roles/${selectedRoleId}/generate`);
      await loadRoleDetail(selectedRoleId);
      await loadRoles();
      setRightTab('view');
    } catch (e: unknown) {
      setGenerateError(formatApiDetail(e, 'Generation failed'));
    } finally {
      setGenerateBusy(false);
    }
  };

  const selectionHint = useMemo(() => {
    const count = draft.competencies.length;
    if (count < 3) return `Select at least ${3 - count} more`;
    if (count > 5) return 'Too many selected';
    return `${count} selected`;
  }, [draft.competencies.length]);

  const activeStepIndex = sessionSteps.findIndex((step) => step.id === activeStep);
  const activeStepMeta = sessionSteps.find((step) => step.id === activeStep) ?? sessionSteps[0];
  const canGoBack = activeStepIndex > 0;
  const canGoForward = activeStepIndex < sessionSteps.length - 1;
  const hasGeneratedArtifacts = roleDetail?.generated_jd != null;
  const selectedRoleTitle = draft.role_title || roleDetail?.title || 'Untitled Role';
  const selectedRoleListItem = roles.find((role) => role.id === selectedRoleId) ?? null;
  const rolesCountLabel = `${roles.length} role${roles.length === 1 ? '' : 's'}`;

  const goToStep = (direction: -1 | 1) => {
    const index = sessionSteps.findIndex((step) => step.id === activeStep);
    const next = sessionSteps[index + direction];
    if (next) setActiveStep(next.id);
  };

  return (
    <Page layout="full-width">
      <PageHeader
        title="Role Builder"
        description="Build a role in guided steps, then generate a polished job description and interviewer-ready playbook."
        actions={
          canEdit ? (
            <Button variant="secondary" onClick={() => navigate('/talent/role-builder/settings')}>
              <Cog6ToothIcon className="h-4 w-4" />
              Settings
            </Button>
          ) : null
        }
      />
      <PageBody fill padded={false} className="px-8 pb-8">
        {bootstrapError && (
          <Alert variant="error" title="Could not load Role Builder" className="mb-6">
            {bootstrapError}
          </Alert>
        )}

        <div className="flex flex-1 min-h-0 gap-6">
          <aside className="w-full max-w-[320px] shrink-0">
            <Panel className="flex h-full min-h-[540px] flex-col overflow-hidden">
              <PanelHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <PanelTitle>Roles</PanelTitle>
                    <PanelDescription>Choose a role to build or review.</PanelDescription>
                  </div>
                  {!rolesLoading && roles.length > 0 && (
                    <Badge variant="outline" className="shrink-0">
                      {rolesCountLabel}
                    </Badge>
                  )}
                </div>
              </PanelHeader>
              <PanelBody className="flex flex-1 min-h-0 flex-col gap-4">
                {canEdit && (
                  <Button variant="secondary" className="w-full justify-center" onClick={() => void createRole()}>
                    <PlusIcon className="h-4 w-4" />
                    Create role
                  </Button>
                )}

                {createError && (
                  <Alert variant="error" title="Could not create role">
                    {createError}
                  </Alert>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                  {rolesLoading ? (
                    <div className="flex justify-center py-8">
                      <Spinner size="md" />
                    </div>
                  ) : roles.length ? (
                    <ul className="space-y-2">
                      {roles.map((role) => (
                        <li key={role.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRoleId(role.id);
                              setRightTab('edit');
                            }}
                            aria-pressed={selectedRoleId === role.id}
                            className={`group h-[88px] w-full rounded-xl border px-4 py-3 text-left transition-all ${
                              selectedRoleId === role.id
                                ? 'border-eliza-red/40 bg-eliza-red/10 shadow-sm ring-1 ring-eliza-red/15'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-dark-border/50 dark:bg-dark-surface dark:hover:bg-dark-surface-2'
                            }`}
                            title={role.title}
                          >
                            <div className="flex h-full items-start gap-3">
                              <div
                                className={`h-full w-1 shrink-0 rounded-full transition-colors ${
                                  selectedRoleId === role.id
                                    ? 'bg-eliza-red'
                                    : 'bg-gray-200 group-hover:bg-gray-300 dark:bg-dark-border/70 dark:group-hover:bg-dark-border'
                                }`}
                                aria-hidden="true"
                              />
                              <div className="flex min-w-0 flex-1 flex-col justify-between">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-charcoal dark:text-gray-100">
                                      {role.title}
                                    </p>
                                  </div>
                                  <Badge variant={roleStatusVariant(role.status)}>
                                    {roleStatusLabel(role.status)}
                                  </Badge>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <p className="truncate text-xs text-muted-foreground">
                                    Updated {formatRoleUpdatedAt(role.updated_at)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 px-6 text-center dark:border-dark-border/50">
                      <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-500" />
                      <p className="mt-4 text-sm font-medium text-charcoal dark:text-gray-100">
                        No roles yet
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {canEdit
                          ? 'Create your first role to start building guided role content.'
                          : 'Roles created by your team will appear here.'}
                      </p>
                      {canEdit && (
                        <Button variant="secondary" size="sm" className="mt-4" onClick={() => void createRole()}>
                          <PlusIcon className="h-4 w-4" />
                          Create role
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {!canEdit && (
                  <Alert variant="info" title="Read-only access">
                    You can review existing roles, but only editors can create, update, or generate content.
                  </Alert>
                )}
              </PanelBody>
              <PanelFooter className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>{rolesLoading ? 'Loading roles...' : rolesCountLabel}</span>
                <span className="truncate">
                  {selectedRoleListItem ? `Active: ${selectedRoleListItem.title}` : canEdit ? 'Create or select a role' : 'Select a role to review'}
                </span>
              </PanelFooter>
            </Panel>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col gap-6">
            {generateError && (
              <Alert variant="error" title="Action failed">
                {generateError}
              </Alert>
            )}
            {saveError && (
              <Alert variant="warning" title="Autosave issue">
                {saveError}
              </Alert>
            )}

            {!selectedRoleId && (
              <Panel className="flex flex-1 min-h-[540px] flex-col">
                <PanelHeader>
                  <PanelTitle>Get started</PanelTitle>
                  <PanelDescription>
                    Build each role in guided steps, then review the generated job description and interview kit.
                  </PanelDescription>
                </PanelHeader>
                <PanelBody className="flex flex-1 flex-col justify-center">
                  <div className="mx-auto max-w-4xl space-y-8">
                    <div className="text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 dark:bg-dark-surface-2">
                        <DocumentTextIcon className="h-7 w-7 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="mt-4 font-medium text-charcoal dark:text-gray-100">
                        {canEdit ? 'Create a role or select one from the left rail.' : 'Select a role from the left to review its details.'}
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        The workspace keeps role inputs and generated outputs in one place so you can move from draft to publish-ready content without changing context.
                      </p>
                      {canEdit && (
                        <Button className="mt-6" onClick={() => void createRole()}>
                          <PlusIcon className="h-4 w-4" />
                          Create role
                        </Button>
                      )}
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      {sessionSteps.map((step, index) => (
                        <div
                          key={step.id}
                          className="rounded-xl border border-gray-200 bg-white px-4 py-4 dark:border-dark-border/50 dark:bg-dark-surface"
                        >
                          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Step {index + 1}
                          </p>
                          <p className="mt-2 font-medium text-charcoal dark:text-gray-100">{step.label}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{step.helper}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </PanelBody>
              </Panel>
            )}

            {selectedRoleId != null && detailLoading && (
              <Panel className="min-h-[540px]">
                <PanelBody className="flex h-full items-center justify-center py-20">
                  <Spinner size="lg" />
                </PanelBody>
              </Panel>
            )}

            {selectedRoleId != null && !detailLoading && roleDetail && (
              <Tabs
                defaultValue="edit"
                value={rightTab}
                onValueChange={(value) => setRightTab(value as 'edit' | 'view')}
                className="flex min-h-0 flex-1 flex-col"
              >
                <TabsContent value="edit" className="mt-0 flex min-h-0 flex-1 flex-col">
                  <Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <PanelHeader>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <PanelTitle className="truncate">{selectedRoleTitle}</PanelTitle>
                            <Badge variant={roleStatusVariant(roleDetail.status)}>
                              {roleStatusLabel(roleDetail.status)}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <TabsList variant="default" className="w-fit">
                              <TabsTrigger value="edit">Builder</TabsTrigger>
                              <TabsTrigger value="view">Role details</TabsTrigger>
                            </TabsList>
                            <div className="h-5 w-px bg-gray-200 dark:bg-dark-border" />
                            <div className="flex flex-wrap gap-1.5">
                              {sessionSteps.map((step, index) => {
                                const isActive = activeStep === step.id;
                                const isComplete = index < activeStepIndex;

                                return (
                                  <button
                                    key={step.id}
                                    type="button"
                                    onClick={() => setActiveStep(step.id)}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                                      isActive
                                        ? 'border-eliza-red/35 bg-eliza-red/10 text-eliza-red'
                                        : isComplete
                                          ? 'border-eliza-red/20 bg-eliza-red/5 text-charcoal dark:text-gray-100'
                                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-400 dark:hover:text-white'
                                    }`}
                                  >
                                    <span
                                      className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] leading-none ${
                                        isActive
                                          ? 'bg-eliza-red text-white'
                                          : isComplete
                                            ? 'bg-eliza-red/15 text-eliza-red'
                                            : 'bg-gray-100 text-muted-foreground dark:bg-dark-surface-2'
                                      }`}
                                    >
                                      {index + 1}
                                    </span>
                                    <span>{step.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        {canEdit && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => void duplicateRole()}>
                              <Square2StackIcon className="h-4 w-4" />
                              Duplicate
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => void deleteRole()}>
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </Button>
                            <Button variant="brand" onClick={() => void runGenerate()} disabled={generateBusy}>
                              {generateBusy ? 'Generating...' : 'Generate'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </PanelHeader>

                    <PanelBody className="min-h-0 flex-1 overflow-y-auto">
                      <div className="mx-auto w-full max-w-4xl">
                        {activeStep === 'basics' && (
                          <div className="grid gap-6">
                            <div className="space-y-1.5">
                              <Label htmlFor="rb-title">Role title</Label>
                              <p className="text-xs text-muted-foreground">
                                Use the job title candidates and interviewers will recognize immediately.
                              </p>
                              <Input
                                id="rb-title"
                                value={draft.role_title}
                                disabled={!canEdit}
                                onChange={(e) => setDraftField({ role_title: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="rb-why">Why this role exists</Label>
                              <p className="text-xs text-muted-foreground">
                                Explain the business need and the problem this role is expected to solve.
                              </p>
                              <Textarea
                                id="rb-why"
                                rows={5}
                                value={draft.why_exists}
                                disabled={!canEdit}
                                onChange={(e) => setDraftField({ why_exists: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="rb-join">Why join</Label>
                              <p className="text-xs text-muted-foreground">
                                Capture the pitch that makes this role and company compelling right now.
                              </p>
                              <Textarea
                                id="rb-join"
                                rows={5}
                                value={draft.why_join}
                                disabled={!canEdit}
                                onChange={(e) => setDraftField({ why_join: e.target.value })}
                              />
                            </div>
                          </div>
                        )}

                        {activeStep === 'skills' && (
                          <div className="grid gap-6">
                            <div className="space-y-1.5">
                              <Label htmlFor="rb-kpis">KPIs</Label>
                              <p className="text-xs text-muted-foreground">
                                Add one KPI per line so the generated content can clearly describe success measures.
                              </p>
                              <Textarea
                                id="rb-kpis"
                                rows={6}
                                value={kpisText}
                                disabled={!canEdit}
                                onChange={(e) => setKpisText(e.target.value)}
                                onBlur={onKpisBlur}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="rb-skills">Must-have skills</Label>
                              <p className="text-xs text-muted-foreground">
                                Add one must-have skill per line to keep the candidate profile specific and scannable.
                              </p>
                              <Textarea
                                id="rb-skills"
                                rows={6}
                                value={skillsText}
                                disabled={!canEdit}
                                onChange={(e) => setSkillsText(e.target.value)}
                                onBlur={onSkillsBlur}
                              />
                            </div>
                          </div>
                        )}

                        {activeStep === 'competencies' && (
                          <div className="space-y-5">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="space-y-1">
                                <Label>Core competencies</Label>
                                <p className="text-xs text-muted-foreground">
                                  Select between 3 and 5 competencies to anchor the interview process.
                                </p>
                              </div>
                              <Badge variant="outline">{selectionHint}</Badge>
                            </div>
                            <ChipGroup className="max-h-64 overflow-y-auto">
                              {competencies.map((competency) => (
                                <Chip
                                  key={competency.key}
                                  variant="outline"
                                  selected={draft.competencies.includes(competency.key)}
                                  disabled={
                                    !canEdit ||
                                    (!draft.competencies.includes(competency.key) && draft.competencies.length >= 5)
                                  }
                                  onToggle={() => canEdit && toggleCompetency(competency.key)}
                                >
                                  {competency.key}
                                </Chip>
                              ))}
                            </ChipGroup>
                            <div className="grid gap-3 md:grid-cols-2">
                              {draft.competencies.map((competencyKey) => {
                                const competency = competencies.find((item) => item.key === competencyKey);
                                return (
                                  <div
                                    key={competencyKey}
                                    className="rounded-xl border border-gray-200 px-4 py-4 dark:border-dark-border/50"
                                  >
                                    <p className="font-medium text-charcoal dark:text-gray-100">
                                      {competencyKey}
                                    </p>
                                    {competency?.description && (
                                      <p className="mt-1 text-sm text-muted-foreground">{competency.description}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </PanelBody>

                    <PanelFooter className="flex shrink-0 items-center justify-between gap-3">
                      <div className="text-xs text-muted-foreground">
                        Step {activeStepIndex + 1} of {sessionSteps.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={() => goToStep(-1)} disabled={!canGoBack}>
                          Back
                        </Button>
                        {canGoForward ? (
                          <Button variant="secondary" onClick={() => goToStep(1)}>
                            Next
                          </Button>
                        ) : (
                          <Button variant="brand" onClick={() => void runGenerate()} disabled={!canEdit || generateBusy}>
                            {generateBusy ? 'Generating...' : 'Generate artifacts'}
                          </Button>
                        )}
                      </div>
                    </PanelFooter>
                  </Panel>
                </TabsContent>

                <TabsContent value="view" className="mt-0 flex min-h-0 flex-1 flex-col">
                  <Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <PanelHeader>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <PanelTitle className="truncate">{selectedRoleTitle}</PanelTitle>
                            <Badge variant={roleStatusVariant(roleDetail.status)}>
                              {roleStatusLabel(roleDetail.status)}
                            </Badge>
                          </div>
                          <TabsList variant="default" className="w-fit">
                            <TabsTrigger value="edit">Builder</TabsTrigger>
                            <TabsTrigger value="view">Role details</TabsTrigger>
                          </TabsList>
                        </div>
                        {canEdit && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => void duplicateRole()}>
                              <Square2StackIcon className="h-4 w-4" />
                              Duplicate
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => void deleteRole()}>
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </Button>
                            <Button variant="brand" onClick={() => void runGenerate()} disabled={generateBusy}>
                              {generateBusy ? 'Generating...' : 'Generate'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </PanelHeader>
                    <PanelBody className="min-h-0 flex-1 overflow-y-auto space-y-5">
                      {hasGeneratedArtifacts ? (
                        <Tabs defaultValue="jd">
                          <TabsList className="mb-5">
                            <TabsTrigger value="jd">
                              Job description
                            </TabsTrigger>
                            <TabsTrigger value="kit">
                              Interview kit
                            </TabsTrigger>
                            <TabsTrigger value="plain">
                              Plain text
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="jd">
                            <JobDescriptionView document={roleDetail.generated_jd!} />
                          </TabsContent>

                          <TabsContent value="kit">
                            <InterviewKitView interviewKit={roleDetail.generated_interview_kit!} />
                          </TabsContent>

                          <TabsContent value="plain">
                            <div className="space-y-4">
                              <div>
                                <Label className="mb-1.5 block">Job description copy</Label>
                                <Textarea
                                  readOnly
                                  className="min-h-[220px] font-mono text-sm"
                                  value={roleDetail.generated_copy_text?.job_description ?? ''}
                                />
                              </div>
                              <div>
                                <Label className="mb-1.5 block">Interview kit copy</Label>
                                <Textarea
                                  readOnly
                                  className="min-h-[220px] font-mono text-sm"
                                  value={roleDetail.generated_copy_text?.interview_kit ?? ''}
                                />
                              </div>
                            </div>
                          </TabsContent>
                        </Tabs>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 dark:bg-dark-surface-2">
                            <DocumentTextIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                          </div>
                          <p className="font-medium text-charcoal dark:text-gray-100">No generated content yet</p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Fill out the role details in the Edit tab, then click Generate to create the job description and interview kit.
                          </p>
                          <Button variant="secondary" size="sm" className="mt-4" onClick={() => setRightTab('edit')}>
                            Go to Edit
                          </Button>
                        </div>
                      )}
                    </PanelBody>
                  </Panel>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </PageBody>
    </Page>
  );
}

export default RoleBuilderPage;
